// frontend/src/features/ogdb2/metricRegistry.js

/**
 * OGDB2 Metric Registry
 * ----------------------
 * Single source of truth for:
 * - which datapoints exist (metric_code)
 * - how they are typed and displayed
 * - which scenarios they can exist for (assessor/actual/ba)
 * - whether they are drivers (editable/importable) or derived (calculated)
 * - which UI surfaces they should appear in (cockpit, bulk defaults, categories)
 *
 * metric_code MUST match backend metric_code keys exactly.
 */

export const OGDB2_METRIC_CATEGORIES = {
  FACTS_QUICK: "facts_quick",
  PRODUCTION: "production",
  EXPENSES: "expenses",
  PRICE_DECK: "price_deck",
  OWNERSHIP: "ownership",
  VALUATION: "valuation",
};

export const OGDB2_METRIC_KINDS = {
  DRIVER: "driver",
  DERIVED: "derived",
};

export const OGDB2_METRICS = [
  // ---------------- Drivers (canonical inputs that can vary by scenario) ----------------

  // ---------------- PRODUCTION ----------------
  {
    code: "production_boe",
    label: "Production (BOE)",
    unit: "BOE",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION, OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: true,
    bulkDefault: true,
  },
  {
    code: "oil_bbl",
    label: "Oil (bbl)",
    unit: "bbl",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas_mcf",
    label: "Gas (mcf)",
    unit: "mcf",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // Comparable start rates (Assessor/BA are editable; 3P is computed in UI from GasSearch + basis)
  {
    code: "comparable_oil_start_rate",
    label: "Comparable Oil Start Rate",
    unit: "bbl/d",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION],
    editableScenarios: ["assessor", "ba"], // do NOT include "actual" (prevents 3P column from being editable)
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "comparable_gas_start_rate",
    label: "Comparable Gas Start Rate",
    unit: "mcf/d",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION],
    editableScenarios: ["assessor", "ba"], // do NOT include "actual" (prevents 3P column from being editable)
    cockpit: false,
    bulkDefault: false,
  },

  // Basis selectors (stored as scenario "ba"; edited via dropdown inside the 3P column on comparable rows)
  {
    code: "tp_start_rate_basis_oil",
    label: "3P Oil Start Rate Basis",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [], // ✅ do not show as a Production row
    editableScenarios: ["ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "tp_start_rate_basis_gas",
    label: "3P Gas Start Rate Basis",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [], // ✅ do not show as a Production row
    editableScenarios: ["ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // Candidate columns (imported in third_party scenario)
  {
    code: "tp_oil_sr_sep_dec",
    label: "3P Oil SR - Sep-Dec Avg",
    unit: "bbl/d",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION],
    editableScenarios: [],
    cockpit: false,
    bulkDefault: false,
  },
  { code: "tp_oil_sr_last_1", label: "3P Oil SR - Last Month", unit: "bbl/d", type: "number", kind: OGDB2_METRIC_KINDS.DRIVER, categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION], editableScenarios: [], cockpit: false, bulkDefault: false },
  { code: "tp_oil_sr_last_3", label: "3P Oil SR - Last 3 Mo Avg", unit: "bbl/d", type: "number", kind: OGDB2_METRIC_KINDS.DRIVER, categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION], editableScenarios: [], cockpit: false, bulkDefault: false },
  { code: "tp_oil_sr_last_4", label: "3P Oil SR - Last 4 Mo Avg", unit: "bbl/d", type: "number", kind: OGDB2_METRIC_KINDS.DRIVER, categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION], editableScenarios: [], cockpit: false, bulkDefault: false },
  { code: "tp_oil_sr_last_6", label: "3P Oil SR - Last 6 Mo Avg", unit: "bbl/d", type: "number", kind: OGDB2_METRIC_KINDS.DRIVER, categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION], editableScenarios: [], cockpit: false, bulkDefault: false },
  { code: "tp_oil_sr_last_12", label: "3P Oil SR - Last 12 Mo Avg", unit: "bbl/d", type: "number", kind: OGDB2_METRIC_KINDS.DRIVER, categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION], editableScenarios: [], cockpit: false, bulkDefault: false },

  {
    code: "tp_gas_sr_sep_dec",
    label: "3P Gas SR - Sep-Dec Avg",
    unit: "mcf/d",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION],
    editableScenarios: [],
    cockpit: false,
    bulkDefault: false,
  },
  { code: "tp_gas_sr_last_1", label: "3P Gas SR - Last Month", unit: "mcf/d", type: "number", kind: OGDB2_METRIC_KINDS.DRIVER, categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION], editableScenarios: [], cockpit: false, bulkDefault: false },
  { code: "tp_gas_sr_last_3", label: "3P Gas SR - Last 3 Mo Avg", unit: "mcf/d", type: "number", kind: OGDB2_METRIC_KINDS.DRIVER, categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION], editableScenarios: [], cockpit: false, bulkDefault: false },
  { code: "tp_gas_sr_last_4", label: "3P Gas SR - Last 4 Mo Avg", unit: "mcf/d", type: "number", kind: OGDB2_METRIC_KINDS.DRIVER, categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION], editableScenarios: [], cockpit: false, bulkDefault: false },
  { code: "tp_gas_sr_last_6", label: "3P Gas SR - Last 6 Mo Avg", unit: "mcf/d", type: "number", kind: OGDB2_METRIC_KINDS.DRIVER, categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION], editableScenarios: [], cockpit: false, bulkDefault: false },
  { code: "tp_gas_sr_last_12", label: "3P Gas SR - Last 12 Mo Avg", unit: "mcf/d", type: "number", kind: OGDB2_METRIC_KINDS.DRIVER, categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION], editableScenarios: [], cockpit: false, bulkDefault: false },

  // Economic reserves (from raw:*ReservesEconomic)
  {
    code: "oil_reserves_economic",
    label: "Est Oil Reserves (Economic)",
    unit: "bbl",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas_reserves_economic",
    label: "Est Gas Reserves (Economic)",
    unit: "mcf",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRODUCTION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // ---------------- PRICE DECK ----------------
  {
    code: "oil_price",
    label: "Oil Price",
    unit: "$/bbl",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRICE_DECK, OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: true,
    bulkDefault: true,
  },
  {
    code: "gas_price",
    label: "Gas Price",
    unit: "$/mcf",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRICE_DECK, OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: true,
    bulkDefault: true,
  },
  {
    code: "ngl_price",
    label: "NGL Price",
    unit: "$/bbl",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRICE_DECK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "price_escalation_pct",
    label: "Price Escalation",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.PRICE_DECK, OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

   // ---------------- EXPENSES ----------------
  // We keep imports flexible but normalize analysis to Comparable LOE.
  //
  // Canon:
  // - Net LOE = OpEx
  // - Gross LOE = OpEx + Marketing
  // - Ad Valorem is NEVER part of LOE (separate)
  //
  // Assessor provides imported LOE only (metric_code: "loe").
  // Comparable LOE is derived client-side for Bulk Analysis + BAID detail display.

  {
    code: "loe",
    label: "LOE (Imported)",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    // IMPORTANT: keep this OUT of EXPENSES so Bulk defaults don't show it as the LOE number.
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // ✅ Derived metric used in Bulk Analysis (the one you want to see)
  {
    code: "loe_comparable",
    label: "Comparable LOE",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DERIVED,
    categories: [OGDB2_METRIC_CATEGORIES.EXPENSES],
    editableScenarios: [], // derived
    cockpit: true,
    bulkDefault: true,
  },

  // Basis selector (controls Comparable LOE math)
  {
    code: "loe_basis",
    label: "LOE Basis",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.EXPENSES],
    editableScenarios: ["assessor"],
    cockpit: false,
    bulkDefault: false,
  },

  // Simplified buckets
  {
    code: "opex",
    label: "OpEx",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.EXPENSES],
    editableScenarios: ["actual"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "marketing",
    label: "Marketing",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.EXPENSES],
    // BA can now carry a marketing ask (flows to lease-summary export as
    // the "BA Ask - Marketing Expenses" column).
    editableScenarios: ["actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "ad_valorem",
    label: "Ad Valorem",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.EXPENSES],
    editableScenarios: ["actual"],
    cockpit: false,
    bulkDefault: false,
  },
  
  {
    code: "loe_variable_share_pct",
    label: "LOE Variable Share",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // ---------------- OWNERSHIP / INTEREST ----------------
  {
    code: "working_interest_pct",
    label: "Working Interest",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.OWNERSHIP, OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: true,
  },
  {
    code: "royalty_interest_pct",
    label: "Royalty Interest",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.OWNERSHIP],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "net_revenue_interest_pct",
    label: "Net Revenue Interest (NRI)",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.OWNERSHIP],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // Optional: if your raw sources include dollar-value versions of interest
  {
    code: "working_interest_value",
    label: "Working Interest Value",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.OWNERSHIP],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "royalty_interest_value",
    label: "Royalty Interest Value",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.OWNERSHIP],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  
   {
    code: "prior_year_working_interest_value",
    label: "Prior Year WI Value",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.OWNERSHIP],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "prior_year_royalty_interest_value",
    label: "Prior Year RI Value",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.OWNERSHIP],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // ---------------- VALUATION ASSUMPTIONS ----------------
  {
    code: "decline_rate",
    label: "Decline Rate",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: true,
    bulkDefault: false,
  },
  {
    code: "discount_rate",
    label: "Discount Rate",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: true,
    bulkDefault: false,
  },

  // Product-specific discount rates (from "Oil Discount Rate" / "Gas Discount Rate")
  {
    code: "oil_discount_rate",
    label: "Oil Discount Rate",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas_discount_rate",
    label: "Gas Discount Rate",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // Product-specific decline parameters (from raw:Gas1Decline* / raw:Oil1Decline*)
  {
    code: "oil_decline_rate",
    label: "Oil Decline Rate",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "oil_decline_n_factor",
    label: "Oil Decline N-Factor",
    unit: null,
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas_decline_rate",
    label: "Gas Decline Rate",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas_decline_n_factor",
    label: "Gas Decline N-Factor",
    unit: null,
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // Other “facts” you want to canonicalize for mapping (text + misc numeric)
  
  {
    code: "taxable_current_year",
    label: "Taxable Value (Current Year)",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "taxable_prior_year",
    label: "Taxable Value (Prior Year)",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  
  {
    code: "market_current_year",
    label: "Market Value (Current Year)",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "market_prior_year",
    label: "Market Value (Prior Year)",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  
  // ✅ Appraisal Roll W/R/O buckets (Capitol roll rows split by ValueType)
  {
    code: "market_current_year_w",
    label: "Market Value (CY) - W",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "market_current_year_r",
    label: "Market Value (CY) - R",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "market_current_year_o",
    label: "Market Value (CY) - O",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  {
    code: "market_prior_year_w",
    label: "Market Value (PY) - W",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "market_prior_year_r",
    label: "Market Value (PY) - R",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "market_prior_year_o",
    label: "Market Value (PY) - O",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  {
    code: "taxable_current_year_w",
    label: "Taxable Value (CY) - W",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "taxable_current_year_r",
    label: "Taxable Value (CY) - R",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "taxable_current_year_o",
    label: "Taxable Value (CY) - O",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  {
    code: "taxable_prior_year_w",
    label: "Taxable Value (PY) - W",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "taxable_prior_year_r",
    label: "Taxable Value (PY) - R",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "taxable_prior_year_o",
    label: "Taxable Value (PY) - O",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  
  {
    code: "taxing_jurisdictions",
    label: "Taxing Jurisdictions",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "legal_description",
    label: "Legal Description",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  
  {
    code: "appraisal_district",
    label: "Appraisal District",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "lease_name",
    label: "Lease Name",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "lease_number",
    label: "Lease Number",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "product_cd",
    label: "Product Code",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "client_name",
    label: "Client Name",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "economic_life_years",
    label: "Economic Life (Years)",
    unit: "years",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "net_equipment_value",
    label: "Net Equipment Value",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "number_of_wells",
    label: "Number of Wells",
    unit: "count",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // ---------------- LEASE SUMMARY META / IDs ----------------
  {
    code: "audit_timestamp",
    label: "Audit Timestamp",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "audit_user_code",
    label: "Audit User Code",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "client_code",
    label: "Client Code",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "appraisal_year",
    label: "Appraisal Year",
    unit: "year",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "district_number",
    label: "District Number",
    unit: null,
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "division_order_key",
    label: "Division Order Key",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "operator_number",
    label: "Operator Number",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "operator_name",
    label: "Operator Name",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "field_number",
    label: "Field Number",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "field_name",
    label: "Field Name",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.FACTS_QUICK],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // ---------------- OWNERSHIP EXTRAS ----------------
  {
    code: "county_working_interest_value",
    label: "County Working Interest Value",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.OWNERSHIP],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "county_royalty_interest_value",
    label: "County Royalty Interest Value",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.OWNERSHIP],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "composite_royalty_interest_ror",
    label: "Composite Royalty Interest ROR",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.OWNERSHIP, OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "participation",
    label: "Participation",
    unit: null,
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.OWNERSHIP],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // ---------------- VALUATION EXTRAS ----------------
  {
    code: "capex",
    label: "CapEx",
    unit: "$",
    type: "money",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "co2",
    label: "CO2",
    unit: null,
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // ---------------- SEGMENT-LEVEL DATES + DECLINE (2/3) ----------------
  {
    code: "oil1_projection_start_date",
    label: "Oil1 Projection Start Date",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas1_projection_start_date",
    label: "Gas1 Projection Start Date",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  {
    code: "oil2_projection_start_date",
    label: "Oil2 Projection Start Date",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "oil2_projection_start_rate",
    label: "Oil2 Projection Start Rate",
    unit: "bbl/d",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "oil2_decline_rate",
    label: "Oil2 Decline Rate",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "oil2_decline_n_factor",
    label: "Oil2 Decline N-Factor",
    unit: null,
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  {
    code: "gas2_projection_start_date",
    label: "Gas2 Projection Start Date",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas2_projection_start_rate",
    label: "Gas2 Projection Start Rate",
    unit: "mcf/d",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas2_decline_rate",
    label: "Gas2 Decline Rate",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas2_decline_n_factor",
    label: "Gas2 Decline N-Factor",
    unit: null,
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  {
    code: "oil3_projection_start_date",
    label: "Oil3 Projection Start Date",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "oil3_projection_start_rate",
    label: "Oil3 Projection Start Rate",
    unit: "bbl/d",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "oil3_decline_rate",
    label: "Oil3 Decline Rate",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "oil3_decline_n_factor",
    label: "Oil3 Decline N-Factor",
    unit: null,
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  {
    code: "gas3_projection_start_date",
    label: "Gas3 Projection Start Date",
    unit: null,
    type: "text",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas3_projection_start_rate",
    label: "Gas3 Projection Start Rate",
    unit: "mcf/d",
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas3_decline_rate",
    label: "Gas3 Decline Rate",
    unit: "%",
    type: "percent",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },
  {
    code: "gas3_decline_n_factor",
    label: "Gas3 Decline N-Factor",
    unit: null,
    type: "number",
    kind: OGDB2_METRIC_KINDS.DRIVER,
    categories: [OGDB2_METRIC_CATEGORIES.VALUATION],
    editableScenarios: ["assessor", "actual", "ba"],
    cockpit: false,
    bulkDefault: false,
  },

  // ---------------- Derived (future) ----------------
  // Keep registry clean until formulas are implemented.
];

const byCode = new Map(OGDB2_METRICS.map((m) => [m.code, m]));

export function getMetricDef(code) {
  return byCode.get(code) || null;
}

export function getMetricsByCategory(categoryKey) {
  return OGDB2_METRICS.filter((m) => (m.categories || []).includes(categoryKey));
}

export function getDriverMetrics() {
  return OGDB2_METRICS.filter((m) => m.kind === OGDB2_METRIC_KINDS.DRIVER);
}

export function getCockpitDriverMetrics() {
  return OGDB2_METRICS.filter((m) => m.kind === OGDB2_METRIC_KINDS.DRIVER && m.cockpit);
}

export function getBulkDefaultMetricCodes() {
  return OGDB2_METRICS.filter((m) => m.kind === OGDB2_METRIC_KINDS.DRIVER && m.bulkDefault).map((m) => m.code);
}

/**
 * Fallback unit if backend didn't send unit for a metric.
 */
export function getUnitForMetric(code, resolvedUnit) {
  if (resolvedUnit) return resolvedUnit;
  const def = getMetricDef(code);
  return def?.unit || null;
}
